/**
 * Contact Grouping V2 Migration Runner — Admin Diagnostics card.
 *
 * Converts every contact from single-group (`group_id`) to multi-group
 * (`group_ids: List[str]`). Two-phase, button-driven, typed-confirmation.
 * Super-admin only at the UI layer; endpoints are tenant-scoped via JWT.
 *
 * Phases:
 *   1. Dry-Run  →  GET /api/contact-grouping-v2/dry-run   (no writes)
 *   2. Apply    →  POST /api/contact-grouping-v2/apply    (typed APPLY)
 *
 * Emits CustomEvent 'contact-grouping-v2-batch-applied' so the sibling
 * V2 rollback card auto-refreshes and auto-expands.
 *
 * data-testids:
 *   contact-grouping-v2-runner-card
 *   contact-grouping-v2-runner-toggle
 *   contact-grouping-v2-runner-dryrun-btn
 *   contact-grouping-v2-runner-summary
 *   contact-grouping-v2-runner-apply-confirm-input
 *   contact-grouping-v2-runner-apply-btn
 *   contact-grouping-v2-runner-apply-result
 *   contact-grouping-v2-runner-batch-id
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Play, ChevronDown, ChevronRight, Loader2, CheckCircle2,
  AlertTriangle, Copy, Layers, ArrowRightLeft,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ContactGroupingV2RunnerCard() {
  const [hidden, setHidden] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [expanded, setExpanded] = useState(false);

  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunAt, setDryRunAt] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((res) => {
        const user = res.data?.user || res.data || {};
        if (user.is_super_admin) {
          setHidden(false);
          setAdminEmail(user.email || '');
        }
      })
      .catch(() => {});
  }, []);

  const runDryRun = useCallback(async () => {
    setDryRunning(true);
    setDryRunResult(null);
    setApplyResult(null);
    setConfirmInput('');
    try {
      const res = await axios.get(
        `${API}/api/contact-grouping-v2/dry-run`, { headers: authHeaders() },
      );
      setDryRunResult(res.data);
      setDryRunAt(new Date());
      toast.success('V2 dry-run complete — review the plan below');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'V2 dry-run failed');
    } finally {
      setDryRunning(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    if (confirmInput.trim() !== 'APPLY') {
      toast.error('Type APPLY exactly to confirm');
      return;
    }
    if (!dryRunResult) {
      toast.error('Run a dry-run first');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/api/contact-grouping-v2/apply`, {}, { headers: authHeaders() },
      );
      setApplyResult(res.data);
      setConfirmInput('');
      toast.success(`V2 applied — batch ${res.data.batch_id.slice(0, 8)}…`);
      window.dispatchEvent(new CustomEvent(
        'contact-grouping-v2-batch-applied',
        { detail: { batch_id: res.data.batch_id } },
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'V2 apply failed');
    } finally {
      setApplying(false);
    }
  }, [confirmInput, dryRunResult]);

  const copyBatchId = useCallback(async () => {
    if (!applyResult?.batch_id) return;
    try {
      await navigator.clipboard.writeText(applyResult.batch_id);
      toast.success('batch_id copied');
    } catch {
      toast.error('Copy failed');
    }
  }, [applyResult]);

  if (hidden) return null;

  const s = dryRunResult?.summary;
  const toConvert = dryRunResult?.to_convert || [];
  const conflicting = dryRunResult?.conflicting || [];
  const orphans = dryRunResult?.orphan_group_refs || [];

  return (
    <Card className="border-indigo-200" data-testid="contact-grouping-v2-runner-card">
      <CardHeader
        className="cursor-pointer flex flex-row items-start justify-between gap-4"
        onClick={() => setExpanded(!expanded)}
        data-testid="contact-grouping-v2-runner-toggle"
      >
        <div className="flex items-start gap-3">
          {expanded ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              Contact Grouping V2 Migration (single → multi-group)
              <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 border ml-1">
                super-admin · tenant-scoped
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              One-time conversion from the legacy <code className="px-1 mx-0.5 text-[10px] bg-stone-100 rounded">contact.group_id</code> single-link
              model to the new <code className="px-1 mx-0.5 text-[10px] bg-stone-100 rounded">contact.group_ids: []</code> multi-group model.
              Backwards-compatible: <code className="text-[10px] bg-stone-100 px-1 rounded">group_id</code> remains as a synced shadow of
              the primary <code className="text-[10px] bg-stone-100 px-1 rounded">group_ids[0]</code>.
              {adminEmail && (
                <span className="block mt-1 text-[11px] text-stone-500">
                  Signed in as <strong>{adminEmail}</strong>
                </span>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {/* Phase 1: Dry-Run */}
          <div className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Step 1 · Dry-Run</p>
                <p className="text-xs text-stone-600">
                  Read-only. No writes. Returns the conversion plan for review.
                </p>
              </div>
              <Button
                size="sm"
                onClick={runDryRun}
                disabled={dryRunning}
                data-testid="contact-grouping-v2-runner-dryrun-btn"
              >
                {dryRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                Run V2 Dry-Run
              </Button>
            </div>

            {s && (
              <div
                className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-stone-200"
                data-testid="contact-grouping-v2-runner-summary"
              >
                <Stat label="Total contacts" value={s.total_contacts} />
                <Stat label="Already multi" value={s.already_multi} />
                <Stat label="To convert" value={s.to_convert} highlight />
                <Stat label="Init group_ids: []" value={s.to_initialize_empty} />
                <Stat label="Conflicting group_id ≠ group_ids" value={s.conflicting} warn={s.conflicting > 0} />
                <Stat label="Orphan group refs" value={s.orphan_group_refs} warn={s.orphan_group_refs > 0} />
                <Stat label="Total writes" value={s.total_writes} highlight />
                {dryRunAt && (
                  <Stat
                    label="Dry-run captured"
                    value={dryRunAt.toLocaleTimeString()}
                  />
                )}
              </div>
            )}

            {dryRunResult && (
              <div className="space-y-2 pt-2 border-t border-stone-200">
                <DetailBlock
                  title={`Contacts to convert (${toConvert.length}${dryRunResult.to_convert_more ? `, +${dryRunResult.to_convert_more} more` : ''})`}
                  color="sky"
                  items={toConvert.slice(0, 100).map(c => `${c.preview_name || c.id}  ·  group_id=${c.group_id}`)}
                  more={dryRunResult.to_convert_more || 0}
                />
                {conflicting.length > 0 && (
                  <DetailBlock
                    title={`Conflicting (${conflicting.length}) — primary group_id is not in group_ids[]; will be prepended`}
                    color="amber"
                    items={conflicting.map(c => `${c.preview_name || c.id}  ·  group_id=${c.group_id}  ·  group_ids=[${(c.group_ids || []).join(', ')}]`)}
                  />
                )}
                {orphans.length > 0 && (
                  <DetailBlock
                    title={`Orphan references (${orphans.length}) — points at a group that no longer exists; will be cleared`}
                    color="rose"
                    items={orphans.map(o => `${o.preview_name || o.id}  ·  group_id=${o.group_id}  (no such group)`)}
                  />
                )}
              </div>
            )}
          </div>

          {/* Phase 2: Apply */}
          <div className={`rounded-md border p-3 space-y-3 ${
            dryRunResult ? 'border-rose-200 bg-rose-50/40' : 'border-stone-200 bg-stone-50/60 opacity-60'
          }`}>
            <div>
              <p className="text-sm font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                Step 2 · Apply
              </p>
              <p className="text-xs text-stone-600">
                Writes the V2 conversion under a fresh <code className="text-[10px] bg-stone-100 px-1 rounded">batch_id</code>.
                Reversible via the V2 rollback card below.
              </p>
            </div>
            {!dryRunResult && (
              <p className="text-xs text-stone-500 italic">Run a V2 dry-run first.</p>
            )}
            {dryRunResult && !applyResult && (
              <div className="space-y-2">
                <p className="text-xs text-rose-900">
                  Type <strong>APPLY</strong> to confirm. This writes <strong>{s?.total_writes}</strong> contact{s?.total_writes === 1 ? '' : 's'}.
                </p>
                <Input
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder="APPLY"
                  className="font-mono text-xs max-w-[200px]"
                  data-testid="contact-grouping-v2-runner-apply-confirm-input"
                />
                <Button
                  size="sm"
                  onClick={runApply}
                  disabled={applying || confirmInput.trim() !== 'APPLY'}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="contact-grouping-v2-runner-apply-btn"
                >
                  {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                  Apply V2 migration
                </Button>
              </div>
            )}
            {applyResult && (
              <div
                className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2"
                data-testid="contact-grouping-v2-runner-apply-result"
              >
                <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  V2 applied successfully
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-600">batch_id:</span>
                  <code
                    className="text-xs bg-white border border-stone-200 rounded px-1.5 py-0.5"
                    data-testid="contact-grouping-v2-runner-batch-id"
                  >
                    {applyResult.batch_id}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyBatchId} className="h-6 px-2">
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  <Stat label="Converted" value={applyResult.converted} />
                  <Stat label="Init empty" value={applyResult.initialized_empty} />
                  <Stat label="Conflicts resolved" value={applyResult.conflict_resolved} />
                  <Stat label="Orphans cleared" value={applyResult.orphans_cleared} />
                  <Stat label="Snapshots written" value={applyResult.snapshots_written} />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function Stat({ label, value, highlight, warn }) {
  return (
    <div className={`rounded-md p-2 ${
      warn ? 'bg-amber-50 border border-amber-200'
        : highlight ? 'bg-sky-50 border border-sky-200'
          : 'bg-white border border-stone-200'
    }`}>
      <p className="text-[10px] uppercase tracking-wide text-stone-500">{label}</p>
      <p className={`text-sm font-semibold ${
        warn ? 'text-amber-900' : highlight ? 'text-sky-900' : 'text-stone-900'
      }`}>
        {value ?? 0}
      </p>
    </div>
  );
}

function DetailBlock({ title, items, color = 'stone', more = 0 }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  const colorMap = {
    sky: 'border-sky-200 bg-sky-50/40',
    amber: 'border-amber-200 bg-amber-50/40',
    rose: 'border-rose-200 bg-rose-50/40',
    stone: 'border-stone-200 bg-stone-50',
  };
  return (
    <div className={`rounded-md border ${colorMap[color]} text-xs`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-2.5 py-1.5 flex items-center justify-between gap-2 text-left"
      >
        <span className="font-medium">{title}</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open && (
        <ul className="px-3 pb-2 space-y-0.5 max-h-72 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i} className="text-[11px] text-stone-700 font-mono break-words">
              {it}
            </li>
          ))}
          {more > 0 && (
            <li className="text-[11px] italic text-stone-500">…and {more} more</li>
          )}
        </ul>
      )}
    </div>
  );
}
